import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary, Card } from '@kollez/design-system';
import { useMyEligibleDrives, type EligibleDriveRow } from '@/lib/queries';
import { formatIndianDate, formatLPA } from '@kollez/shared';

/**
 * /open-to-me — server-verified list of drives the caller can apply to right now.
 *
 * Consumes GET /students/me/eligible-drives (Pass 11 relationship endpoint).
 * That service checks every rule (branch fit, minCGPA, maxBacklogs, graduation
 * years, tenant scope, sit-out) server-side, so nothing here needs to reason
 * about eligibility — we just render what the server returned.
 */

function partnerName(row: EligibleDriveRow): string {
  const opp = row.opportunity;
  const p = opp?.partner;
  if (!p) return '—';
  if (typeof p === 'string') return p;
  return p.name ?? '—';
}

export function StudentEligibleDrivesPage() {
  const [includeApplied, setIncludeApplied] = useState(false);
  const q = useMyEligibleDrives({ limit: 100, includeApplied });
  const rows = q.data?.drives ?? [];
  const summary = q.data?.summary;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Jobs · Open to me</p>
          <h1 className="h-page">Drives I can apply to</h1>
          <p className="subhead">
            Server-verified against your CGPA, backlogs, branch, and graduation year.
            {summary && summary.total > 0 && (
              <> {summary.eligibleUnapplied} of {summary.total} are still un-applied.</>
            )}
          </p>
        </div>
        <div className="ph-actions">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeApplied}
              onChange={(e) => setIncludeApplied(e.target.checked)}
            />
            Include drives I've already applied to
          </label>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="card kpi">
            <span className="kpi-label">Eligible</span>
            <span className="kpi-value">{summary.total}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Un-applied</span>
            <span className="kpi-value">{summary.eligibleUnapplied}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Applied</span>
            <span className="kpi-value">{summary.appliedTo}</span>
          </div>
        </div>
      )}

      <Card padding="none">
        <AsyncBoundary
          kind="table"
          inCard
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && rows.length === 0}
          emptyTitle="Nothing open to you right now"
          emptyHint="Once your college posts a drive that matches your CGPA, backlogs, branch, and graduation year, it'll show up here."
          onRetry={() => void q.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Drive</th>
                  <th className="hide-mobile">Company</th>
                  <th className="hide-mobile">CTC</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const opp = r.opportunity;
                  const ctc = opp?.compensation?.ctc;
                  const oppId = typeof opp?._id === 'string' ? opp._id : undefined;
                  const to = oppId ? `/opportunities/${oppId}` : `/drives/${r._id}`;
                  return (
                    <tr key={r._id}>
                      <td>
                        <div className="cell-title">
                          <Link to={to} className="link">
                            {opp?.title ?? r.driveName ?? 'Drive'}
                          </Link>
                        </div>
                        {opp?.type && <div className="cell-sub">{opp.type.replace(/_/g, ' ')}</div>}
                      </td>
                      <td className="hide-mobile">{partnerName(r)}</td>
                      <td className="hide-mobile">
                        {ctc?.min
                          ? (ctc.max && ctc.max > ctc.min
                              ? `${formatLPA(ctc.min)} – ${formatLPA(ctc.max)}`
                              : formatLPA(ctc.min))
                          : '—'}
                      </td>
                      <td>{r.driveDate ? formatIndianDate(new Date(r.driveDate)) : '—'}</td>
                      <td>
                        {r.applied
                          ? <span className="badge badge-info">Applied</span>
                          : <span className="badge badge-success">Open</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </Card>
    </div>
  );
}
